import {FC, Fragment} from 'react';
import {notNullOrEmpty} from 'common/utils';
import {createUseStyles} from 'react-jss';
import {useTranslation} from 'react-i18next';
import {Nullable} from 'common/types';
import {getCountryName} from 'features/i18n/helpers';
import {
  Vegadresse as VegadresseModel,
  Stedsadresse as StedsadresseModel,
  Postboksadresse as PostboksadresseModel,
  InternasjonalAdresse as InternasjonalAdresseModel,
  UstrukturertAdresse as UstrukturertAdresseModel,
} from 'features/rettsstiftelse/model/adresse';
import {Aktor} from 'features/rettsstiftelse/model/aktor';
import {useLang} from 'features/i18n/use-lang';

const useAddressStyle = createUseStyles({
  adress: {
    fontStyle: 'normal',
  },
});

const AddressLines: FC<{lines: Nullable<string>[]}> = ({lines}) => {
  const styles = useAddressStyle();
  return (
    <address className={styles.adress}>
      {lines.filter(notNullOrEmpty).map((line, idx) => (
        <Fragment key={idx}>
          {idx !== 0 && <br />} {line}
        </Fragment>
      ))}
    </address>
  );
};

const Vegadresse: FC<{vegadresse: VegadresseModel}> = ({vegadresse}) => (
  <AddressLines
    lines={[
      vegadresse.coNavn,
      [vegadresse.adressenavn, vegadresse.nummer].filter(notNullOrEmpty).join(' '),
      [vegadresse.poststed.postnummer, vegadresse.poststed.navn].filter(notNullOrEmpty).join(' '),
      vegadresse.adressetillegsnavn,
    ]}
  />
);

const Stedsadresse: FC<{stedsadresse: StedsadresseModel}> = ({stedsadresse}) => (
  <AddressLines
    lines={[
      stedsadresse.coNavn,
      stedsadresse.stedsnavn,
      [stedsadresse.poststed.postnummer, stedsadresse.poststed.navn].filter(notNullOrEmpty).join(' '),
    ]}
  />
);

const Postboksadresse: FC<{postboksadresse: PostboksadresseModel}> = ({postboksadresse}) => {
  const {t} = useTranslation(['rettsstiftelser']);
  return (
    <AddressLines
      lines={[
        postboksadresse.coNavn,
        [t('rettsstiftelser:adresse.postboks'), postboksadresse.nummer, postboksadresse.anleggsnavn]
          .filter(notNullOrEmpty)
          .join(' '),
        [postboksadresse.poststed.postnummer, postboksadresse.poststed.navn].filter(notNullOrEmpty).join(' '),
      ]}
    />
  );
};

const InternasjonalAdresse: FC<{internasjonaladresse: InternasjonalAdresseModel}> = ({internasjonaladresse}) => {
  const appLang = useLang();
  return (
    <AddressLines
      lines={[
        internasjonaladresse.bygning,
        [internasjonaladresse.boenhet, internasjonaladresse.etasjenummer].filter(notNullOrEmpty).join(', '),
        [internasjonaladresse.adressenavn, internasjonaladresse.adressenummer].filter(notNullOrEmpty).join(' '),
        internasjonaladresse.postboks,
        [internasjonaladresse.postkode, internasjonaladresse.byEllerStedsnavn].filter(notNullOrEmpty).join(', '),
        internasjonaladresse.distriktEllerBydel,
        internasjonaladresse.region,
        internasjonaladresse.friAdressetekst,
        [internasjonaladresse.landkode, getCountryName(appLang, internasjonaladresse.landkode)]
          .filter(notNullOrEmpty)
          .join(' - '),
      ]}
    />
  );
};

const UstrukturertAdresse: FC<{ustrukturertadresse: UstrukturertAdresseModel}> = ({ustrukturertadresse}) => (
  <AddressLines
    lines={[
      ...ustrukturertadresse.adresse.filter(notNullOrEmpty),
      [ustrukturertadresse.poststed.postnummer, ustrukturertadresse.poststed.navn].join(' '),
    ]}
  />
);

export const RolleAdresse: FC<{rolleInnehaver: Aktor}> = ({rolleInnehaver}) => {
  if ('vegadresse' in rolleInnehaver && rolleInnehaver.vegadresse) {
    return <Vegadresse vegadresse={rolleInnehaver.vegadresse} />;
  } else if ('stedsadresse' in rolleInnehaver && rolleInnehaver.stedsadresse) {
    return <Stedsadresse stedsadresse={rolleInnehaver.stedsadresse} />;
  } else if ('postboksadresse' in rolleInnehaver && rolleInnehaver.postboksadresse) {
    return <Postboksadresse postboksadresse={rolleInnehaver.postboksadresse} />;
  } else if ('internasjonaladresse' in rolleInnehaver && rolleInnehaver.internasjonaladresse) {
    return <InternasjonalAdresse internasjonaladresse={rolleInnehaver.internasjonaladresse} />;
  } else if ('ustrukturertadresse' in rolleInnehaver && rolleInnehaver.ustrukturertadresse) {
    return <UstrukturertAdresse ustrukturertadresse={rolleInnehaver.ustrukturertadresse} />;
  } else {
    return null;
  }
};
